import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
	useRouterState,
	useSearch,
} from "@tanstack/react-router";
import { useContext } from "react";
import z from "zod";
import { EMPTY_ORG_ID } from "@/constants/org";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useUpdateRecordType } from "@/services/record/record.mutations";
import { recordQueryOptions } from "@/services/record/record.options";
import { getRecordTypeDisplayName } from "@/utils/record-type-detail/record-type-detail.utils";
import { RecordTypeDrawerContext } from "@/utils/record-type-detail/record-type-drawer-context";
import { showSuccessToast } from "@/utils/toast.utils";
import type { AdminRecordTypeFullPageTab } from "./AdminRecordTypeFullPage";
import { AdminRecordTypeFullPage } from "./AdminRecordTypeFullPage";

const SearchSchema = z.object({
	tab: z
		.enum(["details", "form", "rules", "activity"])
		.optional()
		.default("details"),
	mode: z.enum(["view", "edit"]).optional().default("view"),
	presentation: z.enum(["drawer", "modal", "full"]).optional().default("full"),
	ruleId: z.string().optional(),
	/** Editor open mode for the rule drawer on `/full/form/rules`. */
	ruleMode: z.enum(["create", "edit", "delete"]).optional(),
	/** Sub-tab inside a single rule's drawer/full view. */
	ruleTab: z.enum(["details", "activity"]).optional(),
	/** Sub-org scope for record-type form definitions (preserved across preview ↔ editor navigation). */
	subOrganizationId: z.string().optional(),
});

const FULL_PARENT =
	"/admin/configuration/recordTypes/$recordTypeId/full" as const;

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/configuration/recordTypes/$recordTypeId/full",
)({
	component: AdminRecordTypeFullView,
	validateSearch: (search) => SearchSchema.parse(search),
});

function AdminRecordTypeFullView() {
	const navigate = useNavigate();
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/configuration/recordTypes/$recordTypeId/full",
	});
	const search = useSearch({
		from: "/_authenticated/(admin-app)/_admin/admin/configuration/recordTypes/$recordTypeId/full",
	});
	const pathname = useRouterState({ select: (s) => s.location.pathname });

	const detailQuery = useQuery(
		recordQueryOptions.recordTypeDetail(params.recordTypeId, {
			organizationId: EMPTY_ORG_ID,
		}),
	);
	const recordType = detailQuery.data ?? null;
	const drawerCtx = useContext(RecordTypeDrawerContext);
	const updateMutation = useUpdateRecordType();

	const formSegment = `/configuration/recordTypes/${params.recordTypeId}/full/form`;
	const onFormRoutes = pathname.includes(formSegment);
	const rulesSegment = `/configuration/recordTypes/${params.recordTypeId}/full/rules/`;
	const onRuleDetailRoute = pathname.includes(rulesSegment);

	const activeTab: AdminRecordTypeFullPageTab = onFormRoutes
		? "form"
		: search.tab;

	if (onRuleDetailRoute || onFormRoutes) {
		return <Outlet />;
	}

	const isEditMode = search.mode === "edit";

	const handleTabChange = (tab: AdminRecordTypeFullPageTab) => {
		if (tab === "form") {
			void navigate({
				to: FULL_PARENT,
				params: { recordTypeId: params.recordTypeId },
				search: {
					tab: "form",
					mode: search.mode,
					presentation: search.presentation,
					subOrganizationId: search.subOrganizationId,
				},
			});
			return;
		}
		void navigate({
			to: FULL_PARENT,
			params: { recordTypeId: params.recordTypeId },
			search: { tab, mode: search.mode, presentation: search.presentation },
		});
	};

	const handleBackToList = () => {
		void navigate({ to: "/admin/configuration/recordTypes" });
	};

	if (!recordType && !detailQuery.isLoading && !detailQuery.isError) {
		return null;
	}

	if (!recordType) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="text-sm text-gray-500">
					{detailQuery.isLoading
						? t("common.actions.loadingEllipsis")
						: t("admin.recordTypes.drawer.errors.notFound")}
				</div>
			</div>
		);
	}

	const fullPageContext = drawerCtx
		? {
				...drawerCtx,
				mode: (search.mode ?? "view") as "view" | "edit",
				setMode: (mode: "view" | "edit") => {
					void navigate({
						to: FULL_PARENT,
						params: { recordTypeId: params.recordTypeId },
						search: { tab: "details", mode, presentation: search.presentation },
					});
				},
				isSaving: updateMutation.isPending,
				saveDraft: async () => {
					if (!drawerCtx.isValid) return;
					try {
						await updateMutation.mutateAsync({
							id: params.recordTypeId,
							data: {
								name: drawerCtx.draftName.trim(),
								description: drawerCtx.draftDescription || null,
								icon: drawerCtx.draftIcon || null,
								subOrganizationIds: null,
								locationRequired: drawerCtx.locationRequired,
							},
							organizationId: EMPTY_ORG_ID,
						});
						showSuccessToast({
							message: t("admin.recordTypes.drawer.details.toast.saveSuccess"),
						});
						void navigate({
							to: FULL_PARENT,
							params: { recordTypeId: params.recordTypeId },
							search: {
								tab: "details",
								mode: "view" as const,
								presentation: search.presentation,
							},
						});
					} catch {
						// Global mutation error handler already shows the error toast
					}
				},
				cancelDraft: () => {
					const name = getRecordTypeDisplayName(recordType);
					drawerCtx.setDraftName(name);
					drawerCtx.setDraftDescription(recordType.description ?? "");
					drawerCtx.setDraftIcon(recordType.icon ?? "");
					void navigate({
						to: FULL_PARENT,
						params: { recordTypeId: params.recordTypeId },
						search: {
							tab: "details",
							mode: "view" as const,
							presentation: search.presentation,
						},
					});
				},
			}
		: null;

	return (
		<RecordTypeDrawerContext.Provider value={fullPageContext}>
			<AdminRecordTypeFullPage
				recordType={recordType}
				activeTab={activeTab}
				onTabChange={handleTabChange}
				onBackToList={handleBackToList}
				isLoading={detailQuery.isLoading}
				isError={detailQuery.isError}
				isEditMode={isEditMode}
			>
				<Outlet />
			</AdminRecordTypeFullPage>
		</RecordTypeDrawerContext.Provider>
	);
}
